import React, { useEffect, useMemo } from 'react';
import type { ActivityViewProps } from '../../core/types/activity.types';
import { GAME_MODE_CATALOG } from '../../core/types/gameMode.types';
import { useRoomContext } from '../../core/client/RoomContext';
import {
  resolveSequencerPresentation,
  sequencerDefaultPrompt,
  sequencerStudentHint,
  sequencerTimelineLabel,
  sequencerStepMarker,
} from '../../core/print/sequencerPresentation';
import { initialStudentOrder } from './sequencer.logic';
import { SequencerTimeline } from './SequencerTimeline';

function Shell({
  badge,
  title,
  prompt,
  children,
  footer,
  host = false,
}: {
  badge?: string;
  title: string;
  prompt: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  host?: boolean;
}) {
  return (
    <div className="plugin-shell rounded-2xl p-5 sm:p-8">
      {badge && (
        <span className="plugin-shell__badge mb-3 inline-block rounded-full px-3 py-1 text-[10px] uppercase tracking-widest">
          {badge}
        </span>
      )}
      <h3 className={`${host ? 'plugin-host-title plugin-host-title--lg' : 'font-display text-xl font-black sm:text-2xl'}`}>
        {title}
      </h3>
      <p className={`mt-2 ${host ? 'plugin-host-prompt plugin-host-prompt--lg' : 'text-base font-bold text-slate-700 sm:text-lg'}`}>
        {prompt}
      </p>
      <div className="mt-6">{children}</div>
      {footer}
    </div>
  );
}

function ProgressPanel({
  submitted,
  total,
  correct,
  size = 'md',
}: {
  submitted: number;
  total: number;
  correct: number;
  size?: 'md' | 'lg';
}) {
  const pct = total > 0 ? Math.round((submitted / total) * 100) : 0;
  const correctPct = submitted > 0 ? Math.round((correct / submitted) * 100) : 0;

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 flex justify-between text-xs font-black uppercase tracking-widest text-slate-600">
          <span>Completaron</span>
          <span>
            {submitted}/{total} · {pct}%
          </span>
        </div>
        <div className="plugin-progress-bar">
          <div className="plugin-progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {submitted > 0 && (
        <div>
          <div className="mb-2 flex justify-between text-xs font-black uppercase tracking-widest text-slate-600">
            <span>Orden correcto</span>
            <span>
              {correct}/{submitted}
            </span>
          </div>
          <div className="plugin-progress-bar plugin-progress-bar--sm">
            <div className="plugin-progress-fill plugin-progress-fill--mint" style={{ width: `${correctPct}%` }} />
          </div>
        </div>
      )}
      {size === 'lg' && (
        <p className="text-center font-display text-4xl font-black text-[var(--sala-electric-purple)]">{pct}%</p>
      )}
    </div>
  );
}

function TimelineReveal({
  items,
  order,
  size = 'md',
  presentation = 'default',
}: {
  items: string[];
  order: number[];
  size?: 'md' | 'lg';
  presentation?: ReturnType<typeof resolveSequencerPresentation>;
}) {
  return <SequencerTimeline items={items} order={order} alternate size={size} presentation={presentation} />;
}

function StudentOrderList({
  items,
  order,
  onMove,
  onSubmit,
  submitted,
  disabled,
  presentation,
  showResults = false,
}: {
  items: string[];
  order: number[];
  onMove: (from: number, to: number) => void;
  onSubmit: () => void;
  submitted: boolean;
  disabled?: boolean;
  presentation: ReturnType<typeof resolveSequencerPresentation>;
  showResults?: boolean;
}) {
  return (
    <div>
      <p className="mb-3 text-xs font-black uppercase tracking-widest text-slate-600">
        {sequencerStudentHint(presentation)}
      </p>
      <ol className="space-y-2">
        {order.map((itemIdx, position) => (
          <li key={`${position}-${itemIdx}`} className="seq-order-item">
            <span
              className={`seq-order-item__index${presentation === 'history' ? ' seq-order-item__index--history' : ''}${presentation === 'music' ? ' seq-order-item__index--music' : ''}`}
            >
              {sequencerStepMarker(presentation, position)}
            </span>
            <span className="flex-1 text-sm font-bold sm:text-base">{items[itemIdx]}</span>
            <div className="flex flex-col gap-0.5">
              <button
                type="button"
                disabled={disabled || position === 0}
                className="rounded border border-slate-950 px-2 py-0.5 text-xs font-black disabled:opacity-30"
                onClick={() => onMove(position, position - 1)}
              >
                ▲
              </button>
              <button
                type="button"
                disabled={disabled || position === order.length - 1}
                className="rounded border border-slate-950 px-2 py-0.5 text-xs font-black disabled:opacity-30"
                onClick={() => onMove(position, position + 1)}
              >
                ▼
              </button>
            </div>
          </li>
        ))}
      </ol>
      {!submitted && !disabled && (
        <button type="button" className="sala-btn-hero mt-5 w-full text-xs" onClick={onSubmit}>
          Confirmar orden
        </button>
      )}
      {submitted && !showResults && (
        <p className="mt-4 text-center text-sm font-black text-emerald-700">Orden enviado · esperando al resto</p>
      )}
    </div>
  );
}

function useSequencerPresentation() {
  const { state } = useRoomContext();
  const subjectTag = state.state?.core.activity.subjectTag;
  return useMemo(() => resolveSequencerPresentation(subjectTag), [subjectTag]);
}

function resolvePrompt(
  prompt: string,
  presentation: ReturnType<typeof resolveSequencerPresentation>,
): string {
  const trimmed = String(prompt || '').trim();
  if (!trimmed || /compás|compas|takadimi|♩|♫/i.test(trimmed)) {
    return sequencerDefaultPrompt(presentation);
  }
  return trimmed;
}

export function SequencerHostView(props: ActivityViewProps<'order'>) {
  const { payload, runtime, phase } = props;
  const presentation = useSequencerPresentation();
  const total = Object.keys(runtime.responses).length;
  const agg = runtime.aggregates;
  const prompt = resolvePrompt(payload.prompt, presentation);

  return (
    <Shell badge={phase === 'RESULTS' ? 'Resultados' : 'En juego'} title={payload.title} prompt={prompt} host>
      <ProgressPanel submitted={agg.submitted} total={Math.max(total, agg.submitted)} correct={agg.correct} />
      {runtime.revealed && (
        <div className="mt-8">
          <p className="mb-3 text-xs font-black uppercase tracking-widest text-slate-600">
            {sequencerTimelineLabel(presentation)}
          </p>
          <TimelineReveal items={payload.items} order={payload.correctOrder} presentation={presentation} />
        </div>
      )}
    </Shell>
  );
}

export function SequencerStudentView(props: ActivityViewProps<'order'>) {
  const { payload, runtime, participantId, phase, dispatch, examAutonomous } = props;
  const presentation = useSequencerPresentation();
  const prompt = resolvePrompt(payload.prompt, presentation);
  const myOrder = participantId ? runtime.responses[participantId] : undefined;
  const submitted = participantId ? Boolean(runtime.finalized[participantId]) : false;
  const waiting = phase === 'PREPARING' || phase === 'LOBBY';
  const showResults = phase === 'RESULTS' || runtime.revealed || (examAutonomous && submitted);

  useEffect(() => {
    if (!participantId || waiting || showResults || myOrder?.length) return;
    void dispatch({ type: 'INIT_ORDER', payload: {} });
  }, [participantId, waiting, showResults, myOrder?.length, dispatch]);

  if (waiting) {
    return (
      <Shell badge={GAME_MODE_CATALOG.order.displayName} title={payload.title} prompt={prompt}>
        <p className="plugin-wait-banner">
          Espera a que el profesor pulse <strong className="text-[var(--sala-electric-purple)]">Comenzar</strong>
        </p>
      </Shell>
    );
  }

  if (showResults) {
    return (
      <Shell badge="Resultados" title={payload.title} prompt={prompt}>
        <TimelineReveal items={payload.items} order={payload.correctOrder} presentation={presentation} />
      </Shell>
    );
  }

  const order = myOrder ?? initialStudentOrder(payload);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length) return;
    const next = [...order];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    void dispatch({ type: 'REORDER', payload: { order: next } });
  };

  return (
    <Shell badge="Secuencias" title={payload.title} prompt={prompt}>
      <StudentOrderList
        items={payload.items}
        order={order}
        onMove={move}
        onSubmit={() => dispatch({ type: 'SUBMIT', payload: {} })}
        submitted={submitted}
        disabled={phase !== 'ACTIVE' || submitted}
        presentation={presentation}
      />
    </Shell>
  );
}

export function SequencerProjectorView(props: ActivityViewProps<'order'>) {
  const { payload, runtime, phase } = props;
  const presentation = useSequencerPresentation();
  const prompt = resolvePrompt(payload.prompt, presentation);
  const agg = runtime.aggregates;
  const revealed = runtime.revealed || phase === 'RESULTS';

  return (
    <Shell
      badge={revealed ? 'Secuencia revelada' : 'Ordenando en vivo'}
      title={payload.title}
      prompt={prompt}
      host
    >
      <ProgressPanel submitted={agg.submitted} total={Math.max(agg.submitted, 1)} correct={agg.correct} size="lg" />
      {revealed && (
        <div className="mt-10">
          <TimelineReveal items={payload.items} order={payload.correctOrder} size="lg" presentation={presentation} />
        </div>
      )}
    </Shell>
  );
}
